import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink, ArrowLeft, FileText, Eye } from "lucide-react";
import { resume } from "../data/content";
import { PrintButton } from "../components/PrintButton";

const PDF_PATH = "/files/Matt_Kolar_Resume.pdf";

export default function Resume() {
  const [view, setView] = useState("read");

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
      {/* Header actions */}
      <div className="no-print flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--accent)]">Résumé</p>
          <h1 className="mt-1 font-head text-3xl font-bold tracking-tight sm:text-4xl">{resume.name}</h1>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <a href={PDF_PATH} download className="ic-btn-primary ic-btn" data-testid="resume-download-pdf-button">
            <Download size={16} /> Download PDF
          </a>
          <a href={PDF_PATH} target="_blank" rel="noopener noreferrer" className="ic-btn" data-testid="resume-open-pdf-button">
            <ExternalLink size={16} /> Open PDF
          </a>
          <Link to="/" className="ic-btn" data-testid="resume-back-to-interview">
            <ArrowLeft size={16} /> Back to interview
          </Link>
        </div>
      </div>

      {/* View toggle */}
      <div className="no-print mt-7 inline-flex rounded-full border border-[var(--border-strong)] p-1" role="group" aria-label="Choose résumé view">
        <button
          type="button"
          onClick={() => setView("read")}
          aria-pressed={view === "read"}
          data-testid="resume-view-read"
          className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            view === "read" ? "bg-[var(--accent)] text-[#05070d]" : "text-[var(--text-2)]"
          }`}
        >
          <FileText size={15} /> Read résumé
        </button>
        <button
          type="button"
          onClick={() => setView("pdf")}
          aria-pressed={view === "pdf"}
          data-testid="resume-view-pdf"
          className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            view === "pdf" ? "bg-[var(--accent)] text-[#05070d]" : "text-[var(--text-2)]"
          }`}
        >
          <Eye size={15} /> View original PDF
        </button>
      </div>

      {/* PDF view */}
      {view === "pdf" && (
        <div className="no-print mt-7">
          <object data={PDF_PATH} type="application/pdf" title="Matt Kolar résumé (original PDF)" className="h-[80vh] w-full rounded-xl border border-[var(--border)]" data-testid="resume-pdf-embed">
            <div className="ic-surface p-8 text-center">
              <p className="text-[var(--text-2)]">Your browser can't display the embedded PDF.</p>
              <div className="mt-4 flex justify-center gap-3">
                <a href={PDF_PATH} target="_blank" rel="noopener noreferrer" className="ic-btn">Open PDF</a>
                <a href={PDF_PATH} download className="ic-btn-primary ic-btn">Download PDF</a>
              </div>
            </div>
          </object>
        </div>
      )}

      {/* HTML transcription */}
      {view === "read" && (
        <div className="mt-8" data-testid="resume-html-view">
          <p className="font-head text-base font-semibold text-[var(--text-2)]">{resume.title}</p>
          <p className="mt-4 max-w-3xl leading-relaxed text-[var(--text-2)]">{resume.summary}</p>

          <section className="mt-10 print-break">
            <h2 className="font-head text-xl font-bold tracking-tight">Experience</h2>
            <div className="mt-5 space-y-8">
              {resume.experience.map((job) => (
                <div key={`${job.company}-${job.dates}`} className="print-break">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <h3 className="font-head text-lg font-semibold">{job.role}</h3>
                    <span className="font-mono text-xs text-[var(--text-muted)]">{job.dates}</span>
                  </div>
                  <p className="mt-0.5 font-medium text-[var(--accent)]">{job.company}</p>
                  <ul className="mt-3 space-y-2">
                    {job.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[var(--text-2)]">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 print-break">
            <h2 className="font-head text-xl font-bold tracking-tight">Education</h2>
            <div className="mt-5 space-y-4">
              {resume.education.map((e) => (
                <div key={e.school}>
                  <h3 className="font-head text-base font-semibold">{e.degree}</h3>
                  <p className="text-[var(--text-2)]">{e.school}</p>
                  {e.detail && <p className="text-sm text-[var(--text-muted)]">{e.detail}</p>}
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 print-break">
            <h2 className="font-head text-xl font-bold tracking-tight">Links</h2>
            <div className="mt-3 flex flex-col gap-1">
              {resume.links.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="ic-link text-sm" data-testid="resume-link-linkedin">
                  {l.label}
                </a>
              ))}
            </div>
          </section>

          <div className="no-print mt-10">
            <PrintButton label="Print / Save résumé" testid="resume-print-button" />
          </div>
        </div>
      )}
    </div>
  );
}
